import {
  anyPass,
  append,
  contains,
  evolve,
  find,
  forEach,
  has,
  identity,
  isEmpty,
  join,
  map,
  mapObjIndexed,
  path,
  pathOr,
  pick,
  pipe,
  propEq,
  reduce,
  repeat,
} from 'ramda';
import { ensureArray } from 'ramda-adjunct';
import { RouteComponentProps } from 'react-router';
import { BiAddMediaActionTarget, BiAddMediaClickOrigin, BiAddMediaTarget, IBiAddMediaFileInfo } from '../constants/Bi';
import { ItemType } from '../constants/ItemType';
import { MediaType } from '../constants/MediaType';
import { ExternalSource, MainSource, Source } from '../constants/Source';
import { extractSystemTagValueFromTag, isFileOriginTag, isVideoSessionIdTag, TagPrefix } from '../constants/SystemTags';
import { generateRandomGuid4 } from '../monitoring/utils';
import { getId } from './common';
import { decodeShutterstockFolderId } from './hoc/extended-sources/withShutterstock.utils';
import { decodeUnsplashFolderId } from './hoc/extended-sources/withUnsplash.utils';
import { TMMGRSettings } from './hoc/withMMGRSettings/withMMGRSettings.types';
import { decodePublicMediaFolderId } from './hoc/withPublicMedia/utils';
import { FileOrigin } from './hooks/useFilesUploader/types';
import { MediaManagerSDKAnyItem } from './media-manager-sdk';
import { isBoard } from './media-manager-sdk/boards';
import { decodeWixSiteFolderId, getFileExtension, isFile } from './media-manager-sdk/items';
import { isMediaFromWix, isShutterstock, isUnsplash } from './media-manager-sdk/sources';
import { getExtension } from './path';
import { RouteMatcher } from './RouteHelpers';
import { getFirstValue } from './set';
import { parseSiteMediaToken, removeTemplatePart } from './siteMediaToken';

export const getItemMediaType = (item: MediaManagerSDKAnyItem): string =>
  isBoard(item) ? MediaType.Board : isFile(item) ? item.mediaType : MediaType.Folder;

export const getItemsMediaType = (items: MediaManagerSDKAnyItem[]) => {
  const mediaTypesSet = new Set(map(getItemMediaType, items));
  const allMediaTypesAreEqual = mediaTypesSet.size === 1;
  return allMediaTypesAreEqual ? getFirstValue(mediaTypesSet) : MediaType.Mixed;
};

export const getItemFormat = (item: MediaManagerSDKAnyItem): string =>
  isBoard(item) ? ItemType.Board : isFile(item) ? getFileExtension(item) : ItemType.Folder;

export const getItemsFormat = (items) => {
  const itemsFormatSet = new Set(map(getItemFormat, items));
  const allMediaTypesAreEqual = itemsFormatSet.size === 1;
  return allMediaTypesAreEqual ? getFirstValue(itemsFormatSet) : MediaType.Mixed;
};

export const extractItemsBatchParams = (items: MediaManagerSDKAnyItem[]) => ({
  correlationId: generateRandomGuid4(),
  batchSize: items.length,
  batch: items.length > 1,
});

export const createFilesBiOptionsMap = (
  files: File[] | FileList | MediaManagerSDKAnyItem[],
  clickOrigin: BiAddMediaClickOrigin,
  target: BiAddMediaTarget,
  fileOrigin?: FileOrigin,
) => {
  const filesOptionsMap: Map<object, IBiAddMediaFileInfo> = new Map();

  // These data will be later used in Bi events that track upload process
  forEach((file: File | MediaManagerSDKAnyItem) => {
    const fileFormat = pathOr<string, string>(getExtension(file.name), ['bi', 'fileFormat'], file);
    const mediaType: MediaType = path(['bi', 'mediaType'], file);
    const isWixVideo =
      clickOrigin === BiAddMediaClickOrigin.WixVideoLibrary || propEq('source', ExternalSource.WixVideo)(file);

    filesOptionsMap.set(file, {
      correlationId: pathOr<string, string>(generateRandomGuid4(), ['bi', 'correlationId'], file),
      batchId: pathOr<string, string>(generateRandomGuid4(), ['bi', 'batchId'], file),
      batch: files.length > 1,
      batchSize: files.length,
      isTags: has('tags', file) && !isEmpty((file as MediaManagerSDKAnyItem).tags),
      clickOrigin,
      fileOrigin,
      target: isWixVideo ? BiAddMediaActionTarget.WixVideoLibrary : target,
      ...(fileFormat ? { fileFormat } : {}),
      ...(mediaType ? { mediaType } : {}),
    });
  }, files);

  return filesOptionsMap;
};

type TChosenItemsInfo = {
  fileType: string;
  fileFormat: string;
  fileId: string;
  mediaType: string;
  name: string;
  mediaOrigin: string;
  fileOrigin: string;
  videoSession: string;
};
type TChosenItemsInfoAggregator = { [key in keyof TChosenItemsInfo]: string[] };

export type TFieldsToPick = (keyof TChosenItemsInfo)[];

export const extractExplorableMediaCategoryIdAndOrientation = (
  source: MainSource,
  folderId: string,
): [string, string] => {
  switch (source) {
    case MainSource.MediaFromWix: {
      const { subCategoryId, orientation } = decodePublicMediaFolderId(folderId);

      return [subCategoryId, orientation];
    }

    case MainSource.Shutterstock: {
      const { category, orientation } = decodeShutterstockFolderId(folderId);

      return [category, orientation];
    }

    case MainSource.Unsplash: {
      const { category, orientation } = decodeUnsplashFolderId(folderId);

      return [category, orientation];
    }

    default:
      return [null, null];
  }
};

export const extractMetaSiteId = (item: MediaManagerSDKAnyItem) => {
  if (path(['siteId'], item)) {
    return { targetFolderMsid: path(['siteId'], item) as string };
  }
  const folderId = path(['folderId'], item) || path(['fileId'], item);
  if (!folderId) {
    return {};
  }
  const decodedFolderId = decodeWixSiteFolderId(folderId as string);
  if (!decodedFolderId) {
    return {};
  }
  const { metaSiteId: targetFolderMsid } = parseSiteMediaToken(decodedFolderId.siteMediaToken);
  return { targetFolderMsid };
};

export const extractCommonBiFieldsFromItems = (
  items: MediaManagerSDKAnyItem | MediaManagerSDKAnyItem[],
  { fileType },
  fieldsToPick: TFieldsToPick = null,
): TChosenItemsInfo => {
  const itemsArray = ensureArray<MediaManagerSDKAnyItem>(items);

  return pipe<
    MediaManagerSDKAnyItem[],
    TChosenItemsInfoAggregator,
    Partial<TChosenItemsInfoAggregator>,
    TChosenItemsInfo
  >(
    reduce<MediaManagerSDKAnyItem, TChosenItemsInfoAggregator>(
      (acc, item) => {
        const { name, source, tags = [] } = item;
        const fileOriginTag = find<string>(isFileOriginTag, tags);
        const isVideoMakerFileOrigin = contains(TagPrefix.VideoMaker, tags);
        const isMediaStudioFileOrigin = contains(TagPrefix.Edited, tags);
        const isShutterstockFileOrigin = contains(TagPrefix.ShutterStock, tags);
        const isUnsplashFileOrigin = contains(TagPrefix.Unsplash, tags);
        const isWixStockFileOrigin = contains(TagPrefix.WixStock, tags);
        const videoSessionTag = find<string>(isVideoSessionIdTag, tags);
        const isWixVideoLibrary = source === ExternalSource.WixVideo;

        return evolve<TChosenItemsInfoAggregator, TChosenItemsInfoAggregator>(
          {
            fileFormat: append(getItemFormat(item)),
            fileId: append(getId(item)),
            mediaType: append(getItemMediaType(item)),
            name: append(name),
            mediaOrigin: append(isWixVideoLibrary ? ExternalSource.WixSite : source),
            fileOrigin: append(
              fileOriginTag
                ? extractSystemTagValueFromTag(fileOriginTag)
                : isVideoMakerFileOrigin
                ? FileOrigin.VideoMaker
                : isMediaStudioFileOrigin
                ? FileOrigin.MediaStudio
                : isShutterstockFileOrigin
                ? FileOrigin.Shutterstock
                : isUnsplashFileOrigin
                ? FileOrigin.Unsplash
                : isWixStockFileOrigin
                ? FileOrigin.WixStock
                : isWixVideoLibrary
                ? FileOrigin.WixVideoLibrary
                : anyPass([isUnsplash, isShutterstock, isMediaFromWix])(source)
                ? source
                : '',
            ),
            videoSession: append(videoSessionTag ? extractSystemTagValueFromTag(videoSessionTag) : ''),
          },
          acc,
        );
      },
      {
        fileType: repeat(fileType, itemsArray.length),
        fileFormat: [],
        fileId: [],
        mediaType: [],
        name: [],
        mediaOrigin: [],
        fileOrigin: [],
        videoSession: [],
      },
    ),
    fieldsToPick ? pick(fieldsToPick) : identity,
    mapObjIndexed(join(',')),
  )(itemsArray);
};

export const extractBiParams = (mmgrSettings: TMMGRSettings) => {
  const { metaSiteId, userId } = parseSiteMediaToken(mmgrSettings.siteMediaToken);
  const { mediaType, namespace, publicMediaRoot, origin, openSource } = mmgrSettings;

  return {
    userId,
    mediaType,
    namespace,
    publicMediaRoot,
    origin,
    openSource,
    metaSiteId: removeTemplatePart(metaSiteId),
  };
};

export const calculateTabName = ({
  pathname,
  source,
}: {
  pathname: RouteComponentProps['location']['pathname'];
  source: Source;
}): string => (RouteMatcher.isSearch(pathname) ? `search_${source}` : source);
